import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/marketing/MarketingLayout';
import { PageSEO } from '../../components/SEO/PageSEO';
import { MapPinIcon, PhoneIcon, ClockIcon } from '@heroicons/react/24/outline';

interface LocationInfo {
  city: string;
  province: string;
  path: string;
  description: string;
  specialties: string[];
}

export default function LocationsHub() {
  const primaryLocations: LocationInfo[] = [
    {
      city: 'Weyburn',
      province: 'Saskatchewan',
      path: '/locations/weyburn',
      description: 'Serving agricultural families, business owners, and professionals throughout Southeast Saskatchewan.',
      specialties: ['Farm & Ranch Insurance', 'Succession Planning', 'Business Protection']
    },
    {
      city: 'Regina',
      province: 'Saskatchewan',
      path: '/locations/regina',
      description: 'Financial planning and insurance solutions for Regina families and businesses.',
      specialties: ['Executive Benefits', 'Estate Planning', 'Corporate Insurance']
    },
    {
      city: 'Lloydminster',
      province: 'SK/AB',
      path: '/locations/lloydminster',
      description: 'Cross-border expertise for families and businesses in the Lloydminster area.',
      specialties: ['Energy Sector', 'Cross-Provincial Planning', 'Agricultural Insurance']
    }
  ];

  const additionalAreas = [
    { name: 'Saskatoon', province: 'SK' },
    { name: 'Moose Jaw', province: 'SK' },
    { name: 'Swift Current', province: 'SK' },
    { name: 'Estevan', province: 'SK' },
    { name: 'Yorkton', province: 'SK' },
    { name: 'Prince Albert', province: 'SK' },
    { name: 'Calgary', province: 'AB' },
    { name: 'Edmonton', province: 'AB' },
    { name: 'Red Deer', province: 'AB' },
    { name: 'Medicine Hat', province: 'AB' },
    { name: 'Lethbridge', province: 'AB' },
    { name: 'Fort McMurray', province: 'AB' }
  ];

  return (
    <MarketingLayout>
      <PageSEO
        title="Service Areas | PCM Paslawski Capital Management Insurance & Planning"
        description="PCM Paslawski Capital Management provides insurance and financial planning services across Saskatchewan and Alberta. Find information about our service areas and how we can help protect your family and business."
        keywords={['insurance Saskatchewan', 'financial planning Alberta', 'insurance broker Regina', 'life insurance Weyburn']}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Serving Saskatchewan & Alberta
            </h1>
            <p className="text-xl text-gray-300 mb-6">
              PCM Paslawski Capital Management provides personalized insurance and financial planning services 
              to families, farmers, and business owners across Western Canada.
            </p>
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <MapPinIcon className="h-5 w-5 text-amber-400" />
                <span>Mobile service - we come to you</span>
              </div>
              <div className="flex items-center gap-2">
                <ClockIcon className="h-5 w-5 text-amber-400" />
                <span>Flexible scheduling</span>
              </div>
              <div className="flex items-center gap-2">
                <PhoneIcon className="h-5 w-5 text-amber-400" />
                <span>Direct advisor access</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Primary Service Areas */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="container max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white mb-4">Primary Service Areas</h2>
          <p className="text-lg text-gray-300 mb-12 max-w-3xl">
            Our financial advisors regularly meet with clients throughout these regions, 
            providing comprehensive insurance and planning services tailored to local needs.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {primaryLocations.map((location) => (
              <Link
                key={location.path}
                to={location.path}
                className="group bg-gray-800/50 backdrop-blur-sm rounded-lg shadow-lg border border-gray-700/50 hover:bg-gray-800/70 hover:shadow-xl transition-all p-6"
              >
                <h3 className="text-2xl font-bold text-white group-hover:text-amber-400 transition-colors mb-2">
                  {location.city}
                </h3>
                <p className="text-sm text-gray-400 mb-4">{location.province}</p>
                <p className="text-gray-300 mb-4">{location.description}</p>
                
                <div className="space-y-2 mb-4">
                  <p className="text-sm font-semibold text-white">Areas of Focus:</p>
                  {location.specialties.map((specialty, index) => (
                    <div key={index} className="text-sm text-gray-300 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-amber-400 rounded-full"></span>
                      {specialty}
                    </div>
                  ))}
                </div>
                
                <div className="text-amber-400 group-hover:text-amber-300 font-medium text-sm">
                  Learn more →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-16 bg-gray-50">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              How We Serve Our Communities
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPinIcon className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">We Come to You</h3>
                <p className="text-gray-600">
                  Meet at your home, office, or favorite coffee shop. 
                  Your convenience is our priority.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ClockIcon className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Flexible Hours</h3>
                <p className="text-gray-600">
                  Early mornings, evenings, and weekends available to 
                  accommodate your schedule.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PhoneIcon className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Direct Access</h3>
                <p className="text-gray-600">
                  Your advisor's direct phone number means help is 
                  always just a call away.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Service Areas */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="container max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white mb-4">Also Serving</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-3xl">
            We regularly travel throughout Saskatchewan and Alberta to meet with clients. 
            Contact us to schedule a consultation in your area.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {additionalAreas.map((area) => (
              <div 
                key={area.name}
                className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-700/50 p-4 text-center"
              >
                <p className="font-medium text-white">{area.name}</p>
                <p className="text-sm text-gray-400">{area.province}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Whether you're in a major city or rural community, we're here to help 
            protect what matters most to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-8 py-3 rounded-lg font-semibold transition-all"
            >
              Schedule a Consultation
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <a 
              href="tel:306-861-4995"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 rounded-lg font-semibold transition-all"
            >
              <PhoneIcon className="h-5 w-5" />
              Call 306-861-4995
            </a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}